import { useEffect, useState, useCallback } from "react";
import { Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  adminListShows, adminCreateShow, adminPatchShow, adminDeleteShow,
} from "../../lib/services";
import { formatApiError } from "../../lib/api";
import useStore from "../../store/useStore";
import { lower } from "../../lib/strings";

const FIELDS_TEXT = [
  ["title", "title*"],
  ["theatre", "theatre"],
  ["venue", "venue"],
  ["company", "company"],
  ["dateLabel", "date label"],
  ["startDate", "start date (YYYY-MM-DD)"],
  ["endDate", "end date (YYYY-MM-DD)"],
  ["status", "status (current/upcoming/past/closed)"],
  ["size", "size (commercial/mid-sized/indie/festival)"],
  ["playwright", "playwright"],
  ["creator", "creator"],
  ["director", "director"],
  ["sourceUrl", "source url"],
  ["posterUrl", "poster url"],
];

const blank = () => ({
  title: "", theatre: "", venue: "", company: "",
  dateLabel: "", startDate: "", endDate: "", status: "upcoming",
  size: "mid-sized", playwright: "", creator: "", director: "",
  sourceUrl: "", posterUrl: "", description: "",
  cast: [], creativeTeam: [], creditsStatus: "incomplete", categories: [],
});

export default function AdminShows() {
  const refreshStoreShows = useStore((s) => s.loadServerState);
  const [shows, setShows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(blank());
  const [search, setSearch] = useState("");

  const refresh = useCallback(async () => {
    try {
      const r = await adminListShows();
      setShows(r.items || []);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const filtered = shows.filter(
    (s) =>
      !search ||
      lower(s.title).includes(lower(search)) ||
      lower(s.theatre).includes(lower(search))
  );

  const startCreate = () => {
    setDraft(blank());
    setCreating(true);
    setOpenId(null);
  };
  const startEdit = (id) => {
    const s = shows.find((x) => x.id === id);
    if (!s) return;
    setDraft({
      ...blank(),
      ...s,
      cast: (s.cast || []).join(", "),
      creativeTeam: (s.creativeTeam || []).map((x) => `${x.role} :: ${x.name}`).join("\n"),
      categories: (s.categories || []).join(", "),
    });
    setOpenId(id);
    setCreating(false);
  };

  const buildPayload = () => {
    const out = { ...draft };
    if (typeof out.cast === "string")
      out.cast = out.cast.split(",").map((c) => c.trim()).filter(Boolean);
    if (typeof out.creativeTeam === "string") {
      out.creativeTeam = out.creativeTeam
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
          const [role, name] = line.split("::").map((x) => x.trim());
          return { role: role || "", name: name || "" };
        });
    }
    if (typeof out.categories === "string")
      out.categories = out.categories.split(",").map((c) => c.trim()).filter(Boolean);
    if (!out.startDate) out.startDate = null;
    if (!out.endDate) out.endDate = null;
    if (!out.posterUrl) out.posterUrl = null;
    if (!out.playwright) out.playwright = null;
    if (!out.creator) out.creator = null;
    if (!out.director) out.director = null;
    return out;
  };

  const onCreate = async () => {
    if (!draft.title.trim()) {
      toast("title required");
      return;
    }
    try {
      await adminCreateShow(buildPayload());
      toast("show created");
      setCreating(false);
      setDraft(blank());
      await Promise.all([refresh(), refreshStoreShows()]);
    } catch (err) {
      toast(formatApiError(err));
    }
  };

  const onSaveEdit = async () => {
    try {
      await adminPatchShow(openId, buildPayload());
      toast("saved");
      setOpenId(null);
      await Promise.all([refresh(), refreshStoreShows()]);
    } catch (err) {
      toast(formatApiError(err));
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("delete this show? (only allowed if no logs/saves)")) return;
    try {
      await adminDeleteShow(id);
      toast("deleted");
      setOpenId(null);
      await Promise.all([refresh(), refreshStoreShows()]);
    } catch (err) {
      toast(formatApiError(err));
    }
  };

  return (
    <section data-testid="admin-shows" className="px-5 py-5">
      <div className="flex gap-2 mb-4">
        <input
          data-testid="admin-shows-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="search title or theatre"
          className="flex-1 bg-transparent border-2 border-white/30 px-3 py-2 font-mono text-xs placeholder:text-white/30 focus:border-acid outline-none"
        />
        <button
          data-testid="admin-shows-new-btn"
          onClick={startCreate}
          className="font-mono text-[11px] lowercase border-2 border-acid text-acid px-3 py-2 hover:bg-acid hover:text-black transition-colors flex items-center gap-1.5"
        >
          <Plus className="w-3.5 h-3.5" /> new show
        </button>
      </div>

      {creating && (
        <ShowForm
          draft={draft}
          setDraft={setDraft}
          onSave={onCreate}
          onCancel={() => { setCreating(false); setDraft(blank()); }}
          mode="create"
        />
      )}

      {!loaded ? (
        <p className="font-mono text-xs text-white/40">loading...</p>
      ) : (
        <ul className="space-y-1.5">
          {filtered.map((s) => (
            <li key={s.id} data-testid={`admin-show-${s.id}`} className="border-b border-white/10">
              {openId === s.id ? (
                <ShowForm
                  draft={draft}
                  setDraft={setDraft}
                  onSave={onSaveEdit}
                  onCancel={() => setOpenId(null)}
                  onDelete={() => onDelete(s.id)}
                  mode="edit"
                />
              ) : (
                <button
                  onClick={() => startEdit(s.id)}
                  data-testid={`admin-show-edit-${s.id}`}
                  className="w-full text-left flex items-baseline justify-between px-3 py-3 hover:bg-acid/5 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-base truncate">{lower(s.title)}</div>
                    <div className="font-mono text-[10px] text-white/50 truncate mt-0.5">
                      {lower(s.theatre)} · {lower(s.dateLabel)}
                    </div>
                  </div>
                  <span className={`font-mono text-[9px] uppercase tracking-wider ml-2 ${
                    s.creditsStatus === "complete" ? "text-acid" : "text-white/50"
                  }`}>
                    {s.creditsStatus}
                  </span>
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ShowForm({ draft, setDraft, onSave, onCancel, onDelete, mode }) {
  const setF = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));
  return (
    <div data-testid={`admin-show-form-${mode}`} className="border-2 border-acid p-4 bg-acid/5 my-3 space-y-3">
      {FIELDS_TEXT.map(([k, label]) => (
        <div key={k}>
          <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">{label}</label>
          <input
            data-testid={`admin-show-${k}`}
            value={draft[k] || ""}
            onChange={setF(k)}
            className="mt-1 w-full bg-transparent border-b-2 border-white/30 px-1 py-2 font-mono text-sm focus:border-acid outline-none"
          />
        </div>
      ))}
      <div>
        <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">description</label>
        <textarea
          data-testid="admin-show-description"
          value={draft.description || ""}
          onChange={setF("description")}
          rows={2}
          className="mt-1 w-full bg-transparent border-2 border-white/30 px-2 py-2 font-mono text-sm focus:border-acid outline-none resize-none"
        />
      </div>
      <div>
        <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">cast (comma-separated)</label>
        <textarea
          data-testid="admin-show-cast"
          value={Array.isArray(draft.cast) ? draft.cast.join(", ") : draft.cast || ""}
          onChange={setF("cast")}
          rows={2}
          className="mt-1 w-full bg-transparent border-2 border-white/30 px-2 py-2 font-mono text-sm focus:border-acid outline-none resize-none"
        />
      </div>
      <div>
        <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
          creative team (one per line: role :: name)
        </label>
        <textarea
          data-testid="admin-show-creativeTeam"
          value={
            Array.isArray(draft.creativeTeam)
              ? draft.creativeTeam.map((x) => `${x.role} :: ${x.name}`).join("\n")
              : draft.creativeTeam || ""
          }
          onChange={setF("creativeTeam")}
          rows={3}
          className="mt-1 w-full bg-transparent border-2 border-white/30 px-2 py-2 font-mono text-sm focus:border-acid outline-none resize-none"
        />
      </div>
      <div>
        <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">categories (comma-separated)</label>
        <input
          data-testid="admin-show-categories"
          value={Array.isArray(draft.categories) ? draft.categories.join(", ") : draft.categories || ""}
          onChange={setF("categories")}
          className="mt-1 w-full bg-transparent border-b-2 border-white/30 px-1 py-2 font-mono text-sm focus:border-acid outline-none"
        />
      </div>
      <div>
        <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">credits status</label>
        <select
          data-testid="admin-show-creditsStatus"
          value={draft.creditsStatus || "incomplete"}
          onChange={setF("creditsStatus")}
          className="mt-1 w-full bg-black border-b-2 border-white/30 px-1 py-2 font-mono text-sm focus:border-acid outline-none"
        >
          <option value="incomplete">incomplete</option>
          <option value="complete">complete</option>
        </select>
      </div>

      <div className="flex flex-wrap gap-2 pt-2">
        <button
          data-testid={`admin-show-${mode}-save`}
          onClick={onSave}
          className="bg-acid text-black font-mono text-sm uppercase tracking-wider px-4 py-2 border-2 border-acid hover:bg-black hover:text-acid transition-colors flex items-center gap-1.5"
        >
          <Save className="w-3.5 h-3.5" />
          {mode === "create" ? "create show" : "save"}
        </button>
        <button
          onClick={onCancel}
          className="font-mono text-xs uppercase tracking-wider text-white/60 hover:text-white px-3 py-2"
        >
          cancel
        </button>
        {onDelete && (
          <button
            data-testid="admin-show-delete"
            onClick={onDelete}
            className="ml-auto font-mono text-[11px] lowercase border-2 border-white/30 text-white/60 px-3 py-2 hover:border-white hover:text-white transition-colors flex items-center gap-1.5"
          >
            <Trash2 className="w-3.5 h-3.5" />
            delete
          </button>
        )}
      </div>
    </div>
  );
}
